import { Check, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KioskStep } from "../../../../../../components/kiosk-step";
import { KioskStatePanel } from "../../../../../../components/kiosk-state-panel";

/**
 * The quiet two-minute notice above the actions.
 *
 * Deliberately a static sentence rather than a running countdown: a ticking clock on a medical
 * kiosk turns an optional convenience into a test the patient can fail. It also states plainly that
 * the check-in is unaffected, which is the fact that actually matters to them.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <NearExpiryNotice />
 * ```
 */
export function NearExpiryNotice() {
  const { t } = useTranslation();
  return (
    <output
      className="flex items-center gap-3 rounded-2xl bg-warning-soft px-5 py-4 text-lg text-foreground"
      aria-live="polite"
      data-testid="patient-info-near-expiry-notice"
    >
      <Clock className="size-6 shrink-0 text-warning-ink" aria-hidden="true" />
      {t("self-service.check-in.patient-info.expiry.notice")}
    </output>
  );
}

/**
 * The expired state: the screen closed for privacy, and the check-in still stands (KC-63). The
 * header keeps the review title (KC-61); "Fertig" is the footer action and "Zurück" is hidden by
 * the flow.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <ExpiredScreen onContinue={() => {}} />
 * ```
 */
export function ExpiredScreen({ onContinue }: { onContinue: () => void }) {
  const { t } = useTranslation();
  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t("self-service.check-in.patient-info.review.title-v2")}
      primaryAction={{
        label: t("common.done"),
        icon: Check,
        tone: "success",
        onClick: onContinue,
        testId: "patient-info-expired-continue-button",
      }}
    >
      <KioskStatePanel
        tone="warning"
        icon={<Clock />}
        title={t("self-service.check-in.patient-info.expiry.title")}
        body={t("self-service.check-in.patient-info.expiry.body-v2")}
        testId="patient-info-expired"
      />
    </KioskStep>
  );
}
